<template>
  <Teleport to="body">
    <div
      v-if="!toastStore.isDisabledHintBox && isReady"
      ref="refBoxContainer"
      class="box-container"
      :style="{ bottom: positionBottom, left: positionLeft }"
    >
      <slot></slot>
      <div class="box-wrapper">
        <div ref="hintBox" class="hint-box">
          <div class="hint-box-border">
            <p class="psmall medium" v-html="messageDisplay" />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
<script setup lang="ts">
import { onMounted, ref, watch, computed, nextTick, onUnmounted } from 'vue';

import { storeToRefs } from 'pinia';

import { useToastStore } from '@/stores/toast.js';

const props = defineProps<{
  refTarget: HTMLElement;
  messageHintBox?: string;
}>();

const toastStore = useToastStore();
const { hintBoxHtml, showHintBox } = storeToRefs(toastStore);

const refBoxContainer = ref<HTMLElement>();
const hintBox = ref(null);

const positionBottom = ref<string>('0px');
const positionLeft = ref<string>('0px');
const isReady = ref<boolean>(true);

const messageDisplay = computed(() => {
  return props.messageHintBox ? props.messageHintBox : hintBoxHtml.value;
});

const updatePosition = () => {
  nextTick(() => {
    if (!refBoxContainer.value || !props.refTarget) {
      isReady.value = false;
      return;
    }

    const rectTarget = props.refTarget.getBoundingClientRect();
    const hintBoxRef = refBoxContainer.value.getBoundingClientRect();

    const boxWidth = hintBoxRef.width || refBoxContainer.value.offsetWidth;

    const viewportWidth = window.innerWidth;
    const offsetParent = refBoxContainer.value.offsetParent;
    const offsetParentRect = offsetParent ? offsetParent.getBoundingClientRect() : { left: 0, top: 0 };

    const margin = 4;

    const targetLeftRel = rectTarget.left - offsetParentRect.left;

    let leftPosition = targetLeftRel;

    const absoluteLeft = leftPosition + offsetParentRect.left;
    if (absoluteLeft < margin) {
      leftPosition = margin - offsetParentRect.left;
    } else if (absoluteLeft + boxWidth > viewportWidth - margin) {
      leftPosition = viewportWidth - boxWidth - margin - offsetParentRect.left;
    }

    const targetCenterXRel = targetLeftRel + rectTarget.width / 2;
    const tailLeftPosition = targetCenterXRel - leftPosition;
    refBoxContainer.value.style.setProperty('--tail-left', `${tailLeftPosition}px`);

    positionLeft.value = `${leftPosition}px`;
    positionBottom.value = rectTarget.height / 2 - 22 + 'px';
    isReady.value = true;
  });
};

onMounted(() => {
  window.addEventListener('resize', updatePosition);
  updatePosition();
  setTimeout(() => {
    showHintBox.value = '';
  }, 5000);
});

onUnmounted(() => {
  window.removeEventListener('resize', updatePosition);
  isReady.value = false;
});

watch(
  () => hintBoxHtml.value,
  () => {
    updatePosition();
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.box-container {
  position: fixed;
  display: inline-block;
  z-index: 9999;
}

.box-wrapper {
  position: relative;
  display: inline-block;
}

.hint-box {
  display: flex;
  align-items: flex-start;
  width: auto;
  max-width: calc(100dvw - 2rem);
  border-radius: 16px;
  z-index: 1;
}

.hint-box-border {
  position: relative;
  z-index: 1;
  padding: 0.375rem 0.75rem;
  // background-color: var(--surface-default);
  color: $color-white;
  background-color: $color-gray-700;
  border-radius: 6px;
  p {
    color: $color-white;
  }
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 20px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0px 5px 8px 5px;
    border-color: transparent transparent $color-gray-700 transparent;
  }
}

.info-icon {
  position: relative;
  fill: var(--font-white);
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .box-container {
    left: 3rem;
  }
}
</style>
